import { useWindowSize } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

/**
 * Shell state. Mirrors Yummy Admin's `store/layout.store.ts` (docs/v2/responsive.md §1.1).
 *
 * The breakpoints are 600 and 1024 — NOT Uno's md/lg defaults — because that is what the
 * reference does, and matching it is what makes the sidebar behave identically.
 */
export const useUiStore = defineStore(
  'ui',
  () => {
    /** The user's deliberate choice. Persisted. */
    const collapsed = ref(false)
    /** Derived: <= 1024 forces the icon rail. NEVER persisted. */
    const forceCollapsed = ref(false)
    /** Derived: < 600 turns the sidebar into Yummy's full-screen menu. NEVER persisted. */
    const mobileMode = ref(false)
    /** The phone menu (Yummy's: the sidebar opened to the full width). Transient. */
    const mobileMenuClosed = ref(true)
    /**
     * A tablet's rail opened to the full sidebar, laid over the page (2026-10-01). Between 600 and
     * 1024 the rail is forced, so the topbar's toggle used to change only the desktop preference
     * and nothing on screen moved; it now opens the full menu here. Transient: a tablet reopens
     * on its rail.
     */
    const railExpanded = ref(false)
    const isDark = ref(false)
    // Transient animation origin; never persisted. Yummy DarkModeContainer.vue.
    // The disc's size is worked out where it is drawn (ApmpThemeTransition): the store ships in
    // every visitor's entry, the disc only with a dashboard.
    const themeOrigin = ref<{ x: number; y: number } | null>(null)
    const themeAnimating = ref(false)
    /**
     * The theme the page is SHOWING, as opposed to `isDark`, the one asked for. They differ for
     * the 800ms the disc takes to cover the screen on the way to dark. Everything that paints —
     * the `.dark` class, Naive's theme (`AppProviders`), the chart palette — follows this one, so
     * the whole page turns in the same frame. Naive used to follow `isDark` and switch at the
     * click: light text on still-light cards, and the page's words vanished for 800ms.
     */
    const appliedDark = ref(false)
    const isFluid = ref(false)
    /**
     * Queued toasts. Naive's `useMessage` only works inside a component under the provider,
     * and a router guard is neither — so guards push here and `AppNotices` drains it. NEVER
     * persisted: a toast about something that happened before a reload is noise.
     */
    const notices = ref<
      { id: number; type: 'info' | 'success' | 'warning' | 'error'; text: string }[]
    >([])
    let noticeId = 0
    /** Yummy defaults flat design on: borders rather than shadows (decisions.md D-012). */
    const flatDesign = ref(true)

    // First visit with no stored preference follows the OS.
    if (typeof window !== 'undefined' && window.matchMedia) {
      isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
    }

    const { width } = useWindowSize()
    watch(
      width,
      (w: number) => {
        forceCollapsed.value = w <= 1024
        mobileMode.value = w < 600
        // A menu opened at one size is not left open at another.
        if (mobileMode.value || !forceCollapsed.value) railExpanded.value = false
        if (!mobileMode.value) mobileMenuClosed.value = true
      },
      { immediate: true },
    )

    /** What the sidebar actually renders as. */
    const sidebarMode = computed<'drawer' | 'rail' | 'full'>(() => {
      if (mobileMode.value) return 'drawer'
      if (forceCollapsed.value || collapsed.value) return 'rail'
      return 'full'
    })

    /**
     * One toggle, three behaviours: the phone menu, the tablet's rail opened over the page, and
     * the desktop's persisted collapse.
     */
    function toggleSidebar() {
      if (mobileMode.value) mobileMenuClosed.value = !mobileMenuClosed.value
      else if (forceCollapsed.value) railExpanded.value = !railExpanded.value
      else collapsed.value = !collapsed.value
    }

    /** Closes whatever menu is open over the page; the desktop preference is not touched. */
    function closeSidebar() {
      mobileMenuClosed.value = true
      railExpanded.value = false
    }

    function notify(type: 'info' | 'success' | 'warning' | 'error', text: string) {
      noticeId += 1
      notices.value.push({ id: noticeId, type, text })
    }

    function takeNotices() {
      const queued = notices.value
      notices.value = []
      return queued
    }

    function setDark(value: boolean) {
      themeAnimating.value = false
      isDark.value = value
    }

    // A watcher rather than a side effect inside setDark: persistedstate rehydrates `isDark`
    // directly after the store is created, which would otherwise leave the DOM out of sync
    // with the restored value on every reload.
    watch(
      isDark,
      (value, _previous, onCleanup) => {
        const apply = () => {
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('dark', value)
          }
          appliedDark.value = value
        }
        if (typeof document === 'undefined') {
          apply()
          return
        }
        // Yummy switches Naive immediately and applies the dark surface at 800ms,
        // while its expanding circle runs for 1000ms. Light mode changes immediately.
        if (!themeAnimating.value) {
          apply()
          return
        }
        const surfaceTimer = value ? window.setTimeout(apply, 800) : undefined
        if (!value) apply()
        const finishTimer = window.setTimeout(() => {
          themeAnimating.value = false
        }, 1000)
        onCleanup(() => {
          window.clearTimeout(surfaceTimer)
          window.clearTimeout(finishTimer)
        })
      },
      { immediate: true },
    )

    function toggleTheme(origin?: { x: number; y: number }) {
      const reduced =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      themeAnimating.value = Boolean(origin && !reduced)
      if (origin && !reduced) themeOrigin.value = origin
      isDark.value = !isDark.value
    }

    return {
      collapsed,
      forceCollapsed,
      mobileMode,
      mobileMenuClosed,
      railExpanded,
      isDark,
      appliedDark,
      themeOrigin,
      themeAnimating,
      isFluid,
      flatDesign,
      sidebarMode,
      notices,
      notify,
      takeNotices,
      toggleSidebar,
      closeSidebar,
      setDark,
      toggleTheme,
    }
  },
  {
    persist: {
      // `mobileMode` and `forceCollapsed` are DERIVED from the viewport. Persisting them is
      // how a desktop user opens the app to a mobile layout (responsive.md §1.1).
      pick: ['collapsed', 'isDark', 'isFluid', 'flatDesign'],
    },
  },
)
